import {
	Button,
	Flex,
	Group,
	MantineStyleProps,
	Text,
	Title
} from "@mantine/core"
import styles from "./CountdownBanner.module.css"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import { AEMSvg } from "@components/AEMSvg"
import { useIsMobile } from "@utils/device.utils"
import { AEMContent } from "@AEM/models/AEMPage.model"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { useRedirect } from "@hooks/useRedirect"
import { eventTrackingItems } from "@utils/eventTracking"

export function CountdownBanner({
	componentData,
	timeRemaining,
	...props
}: MantineStyleProps & {
	componentData: AEMContent
	timeRemaining: {
		days: number
		hours: number
		minutes: number
		seconds: number
	}
}) {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.APPLICATION
	)

	const { icons } = useAEMIcons()
	const learnMoreIcon = icons["chevron_verticle_right.svg"]
	const isMobile = useIsMobile()
	const { handleRedirect } = useRedirect()
	const handleButtonClick = () => {
		if (componentData?.linkDetails?.link) {
			handleRedirect({
				link: componentData?.linkDetails?.link,
				type: componentData?.linkDetails?.type,
				appId: componentData?.linkDetails?.appId?.[0],
				toolCode: componentData?.linkDetails?.toolCode?.[0]
			})
		}
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: "banner_click",
			navLinkText: componentData?.linkDetails?.label || ""
		})
	}

	const bannerTitle = componentData?.title || ""
	const buttonLabel = componentData?.linkDetails?.label || "Learn more"

	const ariaLabel = `Countdown event banner. ${buttonLabel} about ${bannerTitle}`

	return (
		<Flex
			mih={{ base: 150, md: "100%" }}
			direction={{ base: "column", md: "row" }}
			ta="center"
			wrap="nowrap"
			align={"center"}
			{...props}
			justify="space-between"
			w="100%"
			p={{ base: "16px 24px", md: "0 40px 0 24px" }}
			bg="var(--text-color-headline-gradient)"
		>
			<Title
				c="light-dark(white, var(--mantine-color-royal-5))"
				fz={20}
				fw={400}
			>
				{!isMobile && componentData?.header}{" "}
				<Text
					c="light-dark(white, var(--mantine-color-royal-5))"
					fz={20}
					fw={600}
					span
				>
					{componentData?.title}
				</Text>
			</Title>
			<Group gap={8} role="timer" aria-label="Countdown banner">
				<Group className={styles.timerContainer}>
					<Text className={styles.timerLabel}>{timeRemaining.days}</Text>
					<Text className={styles.timerCount}>{dictionary?.daysLabel}</Text>
				</Group>
				<Group className={styles.timerContainer}>
					<Text className={styles.timerLabel}>{timeRemaining.hours}</Text>
					<Text className={styles.timerCount}>{dictionary?.hoursLabel}</Text>
				</Group>
				<Group className={styles.timerContainer}>
					<Text className={styles.timerLabel}>{timeRemaining.minutes}</Text>
					<Text className={styles.timerCount}>{dictionary?.minutesLabel}</Text>
				</Group>
				<Group className={styles.timerContainer}>
					<Text className={styles.timerLabel}>{timeRemaining.seconds}</Text>
					<Text className={styles.timerCount}>{dictionary?.secondsLabel}</Text>
				</Group>
			</Group>
			<Button
				variant="white"
				h={32}
				onClick={() => {
					handleButtonClick()
				}}
				aria-label={ariaLabel}
			>
				<Group
					align="center"
					gap={8}
					justify="center"
					c="var(--mantine-color-royal-5)"
				>
					{componentData?.linkDetails?.label}{" "}
					{learnMoreIcon && (
						<AEMSvg
							svgData={{
								path: learnMoreIcon.path,
								iconColor: "var(--mantine-color-royal-5)"
							}}
							w={12}
							h={12}
						/>
					)}
				</Group>
			</Button>
		</Flex>
	)
}
